import { c as _c } from "react/compiler-runtime";
import * as Headless from '@headlessui/react';
import React, { forwardRef } from 'react';
import { cn } from '../../shared/cn';
import { LoadingSpinner } from '../atoms/LoadingSpinner';
import { Link } from './Link';
const styles = {
  size: {
    xs: ['text-xs px-2 py-1'],
    sm: ['text-sm px-2 py-1'],
    base: ['text-base px-2.5 py-1.5'],
    lg: ['text-base px-3 py-2'],
    xl: ['text-lg px-3 py-2']
  },
  base: ['relative isolate inline-flex items-center justify-center gap-x-2 h-fit rounded-md border font-semibold font-medium stroke-2', 'data-disabled:opacity-50', 'focus-visible:outline-none focus-visible:after:absolute focus-visible:after:inset-[-4px] focus-visible:after:rounded-lg focus-visible:after:border-2 focus-visible:after:border-algonavy-400', '*:data-[slot=icon]:-mx-0 *:data-[slot=icon]:my-0.5 sm:*:data-[slot=icon]:my-1 *:data-[slot=icon]:size-4 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:self-center *:data-[slot=icon]:text-current forced-colors:[--btn-icon:ButtonText] forced-colors:data-hover:[--btn-icon:ButtonText] *:data-[slot=icon]:relative *:data-[slot=icon]:z-10'],
  solid: ['border-(--btn-border) bg-(--btn-bg)', 'shadow-xs shadow-[inset_0_1px_--theme(--color-white/15%)]', 'after:absolute after:inset-0 after:-z-10 after:rounded-[calc(var(--radius-md)-1px)]', 'data-active:after:bg-(--btn-hover-overlay) data-hover:after:bg-(--btn-hover-overlay) [--btn-hover-overlay:var(--color-white)]/10 dark:[--btn-hover-overlay:var(--color-white)]/5', 'data-disabled:shadow-none data-disabled:bg-(--btn-border)', '[--btn-icon:var(--color-current)]'],
  outline: ['bg-backgroundPrimary border-borderPrimary border-1 text-contentPrimary relative isolate', 'after:absolute after:inset-0 after:-z-10 after:rounded-md', 'data-hover:after:bg-backgroundHoverOverlay', '[--btn-icon:var(--color-contentPrimary)]'],
  plain: ['border-transparent text-contentPrimary data-active:bg-zinc-950/5 data-hover:bg-zinc-950/5', ' dark:data-active:bg-white/10 dark:data-hover:bg-white/10', '[--btn-icon:var(--color-contentPrimary)] data-active:[--btn-icon:var(--color-contentPrimary)] data-hover:[--btn-icon:var(--color-contentPrimary)] '],
  plainDestructive: ['border-transparent text-contentRed data-active:bg-backgroundPrimary data-active:bg-backgroundHoverOverlay dark:data-hover:bg-backgroundHoverOverlay data-hover:bg-zinc-950/5', '[--btn-icon:var(--color-contentRed)] data-active:[--btn-icon:var(--color-contentRed)] data-hover:[--btn-icon:var(--color-contentRed)] '],
  marketingPrimary: ['text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-algoprimary)] [--btn-border:var(--color-algoprimary)]/90', '[--btn-icon:var(--color-white)] data-active:[--btn-icon:var(--color-white)] data-hover:[--btn-icon:var(--color-white)]'],
  marketingNeutral: ['text-black [--btn-hover-overlay:var(--color-black)]/5 [--btn-bg:var(--color-slate-100)]', '[--btn-icon:var(--color-black)] data-active:[--btn-icon:var(--color-black)] data-hover:[--btn-icon:var(--color-black)]'],
  marketingOutline: ['text-black bg-backgroundPrimary border-borderPrimary border-1 [--btn-icon:var(--color-black)] data-hover:[--btn-icon:var(--color-black)]', 'data-hover:bg-backgroundHoverOverlay'],
  colors: {
    algoprimary: ['text-white [--btn-bg:var(--color-backgroundInversePrimary)] [--btn-border:var(--color-borderInversePrimary)]'],
    destructive: ['text-white [--btn-bg:var(--color-red-500)] [--btn-border:var(--color-red-500)]'],
    warning: ['text-white dark:text-black [--btn-bg:var(--color-contentYellow)] [--btn-border:var(--color-borderWarning)]'],
    error: ['text-white dark:text-black [--btn-bg:var(--color-contentRed)] [--btn-border:var(--color-borderError)]'],
    success: ['text-white dark:text-black [--btn-bg:var(--color-contentGreen)] [--btn-border:var(--color-borderSuccess)]']
  }
};
type ButtonBaseProps = ({
  color?: keyof typeof styles.colors;
  outline?: never;
  plain?: never;
  plainDestructive?: never;
} | {
  color?: never;
  outline: true;
  plain?: never;
  plainDestructive?: never;
} | {
  color?: never;
  outline?: never;
  plain: true;
  plainDestructive?: never;
} | {
  color?: never;
  outline?: never;
  plain?: never;
  plainDestructive: true;
}) & {
  className?: string;
  children: React.ReactNode;
  size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl';
  marketing?: boolean;
  loading?: boolean;
  rounded?: boolean;
  marketingVariant?: 'primary' | 'neutral' | 'outline';
};
type ButtonLinkProps = ButtonBaseProps & Omit<React.ComponentPropsWithoutRef<typeof Link>, 'className'> & {
  href: string;
};
type ButtonButtonProps = ButtonBaseProps & Omit<Headless.ButtonProps, 'as' | 'className'>;
export type ButtonProps = ButtonLinkProps | ButtonButtonProps;
export type ButtonSize = NonNullable<ButtonBaseProps['size']>;
export const Button = forwardRef(function Button(t0, ref) {
  const $ = _c(57);
  let children;
  let className;
  let marketing;
  let marketingVariant;
  let outline;
  let plain;
  let plainDestructive;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    ({
      color: t1,
      outline,
      plain,
      plainDestructive,
      size: t2,
      marketing,
      marketingVariant,
      loading: t3,
      rounded: t4,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = marketing;
    $[4] = marketingVariant;
    $[5] = outline;
    $[6] = plain;
    $[7] = plainDestructive;
    $[8] = props;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    children = $[1];
    className = $[2];
    marketing = $[3];
    marketingVariant = $[4];
    outline = $[5];
    plain = $[6];
    plainDestructive = $[7];
    props = $[8];
    t1 = $[9];
    t2 = $[10];
    t3 = $[11];
    t4 = $[12];
  }
  const color = t1 === undefined ? "algoprimary" : t1;
  const size = t2 === undefined ? "base" : t2;
  const loading = t3 === undefined ? false : t3;
  const rounded = t4 === undefined ? false : t4;
  let Spinner;
  let T0;
  let buttonProps;
  let renderChildren;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[13] !== children || $[14] !== className || $[15] !== color || $[16] !== loading || $[17] !== marketing || $[18] !== marketingVariant || $[19] !== outline || $[20] !== plain || $[21] !== plainDestructive || $[22] !== props || $[23] !== ref || $[24] !== rounded || $[25] !== size) {
    t8 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const classes = cn(styles.base, styles.size[size ?? "base"], marketing ? cn(styles.solid, marketingVariant === "neutral" ? styles.marketingNeutral : marketingVariant === "outline" ? styles.marketingOutline : styles.marketingPrimary, "w-full lg:w-fit h-10 rounded-sm after:rounded-sm text-nowrap") : outline ? styles.outline : plain ? styles.plain : plainDestructive ? styles.plainDestructive : cn(styles.solid, styles.colors[color ?? "algoprimary"]), loading && "pointer-events-none data-disabled", rounded && "after:rounded-full rounded-full", className);
      let t9;
      if ($[34] !== loading) {
        t9 = loading ? <LoadingSpinner size="button" className="border-current border-t-transparent text-current" /> : null;
        $[34] = loading;
        $[35] = t9;
      } else {
        t9 = $[35];
      }
      Spinner = t9;
      let t10;
      if ($[36] !== children || $[37] !== loading) {
        t10 = () => {
          if (!loading) {
            return children;
          }
          return React.Children.map(children, _temp) as React.ReactNode;
        };
        $[36] = children;
        $[37] = loading;
        $[38] = t10;
      } else {
        t10 = $[38];
      }
      renderChildren = t10;
      if ("href" in props && typeof props.href === "string") {
        const linkProps = props as ButtonLinkProps;
        let t11;
        if ($[39] !== renderChildren) {
          t11 = renderChildren();
          $[39] = renderChildren;
          $[40] = t11;
        } else {
          t11 = $[40];
        }
        let t12;
        if ($[41] !== Spinner || $[42] !== t11) {
          t12 = <TouchTarget>{Spinner}{t11}</TouchTarget>;
          $[41] = Spinner;
          $[42] = t11;
          $[43] = t12;
        } else {
          t12 = $[43];
        }
        t8 = <Link {...linkProps} className={classes} ref={ref as React.ForwardedRef<HTMLAnchorElement>}>{t12}</Link>;
        break bb0;
      }
      buttonProps = props as ButtonButtonProps;
      T0 = Headless.Button;
      t5 = buttonProps;
      t6 = buttonProps.type ?? "button";
      t7 = cn("cursor-default", classes);
    }
    $[13] = children;
    $[14] = className;
    $[15] = color;
    $[16] = loading;
    $[17] = marketing;
    $[18] = marketingVariant;
    $[19] = outline;
    $[20] = plain;
    $[21] = plainDestructive;
    $[22] = props;
    $[23] = ref;
    $[24] = rounded;
    $[25] = size;
    $[26] = Spinner;
    $[27] = T0;
    $[28] = buttonProps;
    $[29] = renderChildren;
    $[30] = t5;
    $[31] = t6;
    $[32] = t7;
    $[33] = t8;
  } else {
    Spinner = $[26];
    T0 = $[27];
    buttonProps = $[28];
    renderChildren = $[29];
    t5 = $[30];
    t6 = $[31];
    t7 = $[32];
    t8 = $[33];
  }
  if (t8 !== Symbol.for("react.early_return_sentinel")) {
    return t8;
  }
  const t9 = loading || buttonProps.disabled;
  let t10;
  if ($[44] !== renderChildren) {
    t10 = renderChildren();
    $[44] = renderChildren;
    $[45] = t10;
  } else {
    t10 = $[45];
  }
  let t11;
  if ($[46] !== Spinner || $[47] !== t10) {
    t11 = <TouchTarget>{Spinner}{t10}</TouchTarget>;
    $[46] = Spinner;
    $[47] = t10;
    $[48] = t11;
  } else {
    t11 = $[48];
  }
  let t12;
  if ($[49] !== T0 || $[50] !== ref || $[51] !== t11 || $[52] !== t5 || $[53] !== t6 || $[54] !== t7 || $[55] !== t9) {
    t12 = <T0 {...t5} type={t6} className={t7} ref={ref} disabled={t9}>{t11}</T0>;
    $[49] = T0;
    $[50] = ref;
    $[51] = t11;
    $[52] = t5;
    $[53] = t6;
    $[54] = t7;
    $[55] = t9;
    $[56] = t12;
  } else {
    t12 = $[56];
  }
  return t12;
});
export function TouchTarget(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="absolute top-1/2 left-1/2 size-[max(100%,2.75rem)] -translate-x-1/2 -translate-y-1/2 [@media(pointer:fine)]:hidden" aria-hidden="true" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <>{t1}{children}</>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function _temp(child) {
  if (!React.isValidElement(child)) {
    return child;
  }
  const childProps = child.props as {
    'data-slot'?: string;
  };
  if (childProps["data-slot"] === "icon") {
    return null;
  }
  return child;
}